import {
  GoalOverviewRoadmapViewOptions,
  RoadmapPlatform,
  RoadmapViewSettings,
  TimelineTimeframes,
  Timestamp,
} from '@griply/types';
import { useCallback } from 'react';

import { useRoadmapNavigation } from './use-roadmap-navigation';

export type UsePersistedRoadmapControlsOptions = {
  roadmap: RoadmapViewSettings | null | undefined;
  onUpdateRoadmap: (roadmap: RoadmapViewSettings) => void;
  today: Timestamp;
  platform: RoadmapPlatform;
};

// referenceDate stays local — persisting scroll position would time-travel the user on reopen.
export const usePersistedRoadmapControls = ({
  roadmap,
  onUpdateRoadmap,
  today,
  platform,
}: UsePersistedRoadmapControlsOptions) => {
  const isWeb = platform === RoadmapPlatform.Web;
  const view =
    (isWeb ? roadmap?.webView : roadmap?.mobileView) ??
    GoalOverviewRoadmapViewOptions.List;
  const timeframe =
    (isWeb ? roadmap?.webTimeframe : roadmap?.mobileTimeframe) ??
    TimelineTimeframes.Year;

  const {
    referenceDate,
    scrollRequest,
    centerTodayIn,
    onToday,
    onNext,
    onPrev,
  } = useRoadmapNavigation({ timeframe, today });

  const setView = useCallback(
    (newView: GoalOverviewRoadmapViewOptions) => {
      onUpdateRoadmap({
        ...(roadmap ?? {}),
        ...(isWeb ? { webView: newView } : { mobileView: newView }),
      });
    },
    [onUpdateRoadmap, roadmap, isWeb],
  );

  const setTimeframe = useCallback(
    (newTimeframe: TimelineTimeframes) => {
      centerTodayIn(newTimeframe);
      onUpdateRoadmap({
        ...(roadmap ?? {}),
        ...(isWeb
          ? { webTimeframe: newTimeframe }
          : { mobileTimeframe: newTimeframe }),
      });
    },
    [onUpdateRoadmap, roadmap, centerTodayIn, isWeb],
  );

  return {
    view,
    setView,
    timeframe,
    setTimeframe,
    referenceDate,
    scrollRequest,
    onPrev,
    onToday,
    onNext,
  };
};
